<template>
    <div style="width: 100%; margin: 10px 0;" class="analysis-data-container">
        <!-- 기존 테이블 -->
        <DataTable
            id="result-data-table"
            class="p-datatable data-table-container"
            :value="tableDatas"
            :scrollable="true"
            :scrollHeight="'500px'"
            showGridlines
        >
            <template #header>
                <div style="display: flex; align-items: center; justify-content: space-between;">
                    <div>
                        <span class="m-0 table-title">분석기간</span>
                    </div>
                    <div>
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="exportToExcel">
                            Excel Export
                        </button>
                    </div>
                </div>
            </template>

            <Column
                field="regDt"
                header="날짜"
                :reorderableColumn="false"
                :style="{ width: '150px' }"
                :headerStyle="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
                :bodyStyle="{
                    'font-size': '13px',
                    'font-weight': 'bold',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                }"
            ></Column>

            <Column
                v-for="(col, index) in dynamicColumns"
                :key="index"
                :field="col.key"
                :reorderableColumn="false"
                :headerStyle="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
                :bodyStyle="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
            >
                <template #header>
                    <div>
                        <div v-if="col.unit">{{ col.key }}({{ unitMap[col.unit] || "-" }})</div>
                    </div>
                </template>
            </Column>
        </DataTable>
        <div class="splitDiv"></div>
        <DataTable
            id="comparison-data-table"
            class="p-datatable data-table-container"
            :value="comparisonTableDatas"
            :scrollable="true"
            :scrollHeight="'500px'"
            showGridlines
        >
            <template #header>
                <div style="display: flex; align-items: center; justify-content: space-between;">
                    <div>
                        <span class="m-0 table-title">비교기간 ({{ compareTitle }})</span>
                    </div>
                    <div>
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="exportComparisonToExcel">
                            Excel Export
                        </button>
                    </div>
                </div>
            </template>

            <Column
                field="regDt"
                header="날짜"
                :reorderableColumn="false"
                :style="{ width: '150px' }"
                :headerStyle="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
                :bodyStyle="{
                    'font-size': '13px',
                    'font-weight': 'bold',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                }"
            ></Column>

            <Column
                v-for="(col, index) in comparisonDynamicColumns"
                :key="index"
                :field="col.key"
                :reorderableColumn="false"
                :headerStyle="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
                :bodyStyle="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
            >
                <template #header>
                    <div>
                        <div v-if="col.unit">{{ col.key }}({{ unitMap[col.unit] || "-" }})</div>
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { mapState } from "vuex";
import moment from "moment";
import excelHelper from "@src/libs/excelHelper.js";

import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {
        DataTable,
        Column,
    },
    props: {
        resultData: { type: Object, required: true },
        detailInfo: { type: Object },
    },
    data() {
        return {
            tableDatas: [],
            dynamicColumns: [],
            comparisonTableDatas: [],
            comparisonDynamicColumns: [],
            popupMessages,
            unitMap: {},
            localResultData: null,
        };
    },
    created() {},
    mounted() {},
    watch: {
        resultData: {
            immediate: true,
            deep: true,
            handler() {
                this.localResultData = this.resultData;
                this.processTableData();
                this.processComparisonData();
                this.unitNomalized();
            },
        },
    },
    computed: {
        ...mapState({
            units: (state) => state.unitCodes,
            compareType: (state) => state.commonCodes.compareType,
        }),
        compareTitle() {
            return this.compareType.find((item) => item.value === this.detailInfo.compareType).text;
        },
    },
    methods: {
        unitNomalized() {
            const tempUnitMap = {};
            Object.keys(this.units).forEach((bigUnit) => {
                this.units[bigUnit].forEach((smallUnit) => {
                    tempUnitMap[smallUnit.value] = smallUnit.text;
                });
            });
            this.unitMap = tempUnitMap;
        },
        timeConvert(reg) {
            const formatMap = {
                HOUR: "HH:mm",
                DAY: "YYYY-MM-DD",
                MONTH: "YYYY-MM-DD",
                YEAR: "YYYY-MM",
            };

            const format = formatMap[this.detailInfo.timeDsvn] || "YYYY-MM-DD HH:mm:ss";

            return moment(new Date(reg)).format(format);
        },
        findUnit(unitSmallCode) {
            for (const key of Object.keys(this.units)) {
                const foundUnit = this.units[key].find((item) => item.value === unitSmallCode);
                if (foundUnit) {
                    return foundUnit.text;
                }
            }
            return null;
        },
        calcUnit(header) {
            let finalUnit = "";
            const dispUnit = header.dispUnit;
            const unitSmallCode = header.unitSmallCode;
            if (dispUnit) {
                finalUnit = dispUnit;
            } else if (!dispUnit && unitSmallCode) {
                finalUnit = unitSmallCode;
            } else {
                finalUnit = "-";
            }

            return finalUnit;
        },
        formatDateTime(dateTimeStr) {
            return dateTimeStr.replace("T", " ");
        },
        formatVariousTime(regDt) {
            const { compareType, timeDsvn } = this.detailInfo;

            if (!/^Avg/.test(compareType)) return this.formatDateTime(regDt);

            const formatMap = {
                QUARTER: () => `${regDt[0]}${regDt[1]}시${regDt[2]}${regDt[3]}분 평균`,
                HOUR: () => `${regDt}시 평균`,
                DAY: () => `${regDt[0]}${regDt[1]}월 ${regDt[2]}${regDt[3]}일 평균`,
                MONTH: () => `${regDt}월 평균`,
            };

            const formatter = formatMap[timeDsvn];
            return formatter ? formatter() : "";
        },
        processTableData() {
            const values = this.localResultData.values || {};
            const regDt = values.regDt || [];
            this.dynamicColumns = Object.keys(values)
                .filter((key) => key !== "regDt")
                .map((key) => {
                    const header = this.localResultData.headers.find((item) => item.key === key);
                    let unit = this.calcUnit(header);

                    return {
                        key,
                        unit,
                    };
                });

            this.tableDatas = regDt.map((date, index) => {
                const rowData = { regDt: this.formatDateTime(date) };
                this.dynamicColumns.forEach((col) => {
                    rowData[col.key] = values[col.key][index] || null;
                });
                return rowData;
            });
        },
        processComparisonData() {
            const comparison = this.localResultData.comparison || {};
            const regDt = comparison.regDt || [];

            const dynamicKeys = Object.keys(comparison)
                .filter((key) => comparison[key]?.value)
                .map((key) => {
                    const header = this.localResultData.headers.find((item) => item.key === key);
                    const unit = this.calcUnit(header);
                    return {
                        key,
                        unit,
                    };
                });

            this.comparisonDynamicColumns = dynamicKeys;

            this.comparisonTableDatas = regDt.map((date, index) => {
                const rowData = { regDt: this.formatVariousTime(date) };
                dynamicKeys.forEach((col) => {
                    const data = comparison[col.key] || {};
                    rowData[col.key] = data.value?.[index] || null;
                });
                return rowData;
            });
        },

        async exportToExcel() {
            try {
                // 헤더 정의
                const headers = [
                    "날짜",
                    ...this.dynamicColumns.map((col) => `${col.key} (${this.findUnit(col.unit)})`),
                ];

                // 데이터 변환
                const excelData = this.tableDatas.map((row) => {
                    const rowData = {
                        날짜: row.regDt,
                    };

                    this.dynamicColumns.forEach((col) => {
                        const headerKey = `${col.key} (${this.findUnit(col.unit)})`;
                        rowData[headerKey] = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : "-";
                    });

                    return rowData;
                });

                // 컬럼 너비 설정
                const columnWidths = {
                    날짜: 20,
                };
                this.dynamicColumns.forEach((col) => {
                    const headerKey = `${col.key} (${this.findUnit(col.unit)})`;
                    columnWidths[headerKey] = 15;
                });

                // 셀 타입 설정
                const cellTypes = {
                    날짜: "string",
                };
                this.dynamicColumns.forEach((col) => {
                    const headerKey = `${col.key} (${this.findUnit(col.unit)})`;
                    cellTypes[headerKey] = "number";
                });

                const fileName = `${this.detailInfo.modelName}.xlsx`;
                const sheetName = "분석기간";

                // excelHelper를 사용하여 워크북 생성 및 다운로드
                await excelHelper.createAndDownloadWorkbook(excelData, headers, fileName, {
                    sheetName,
                    creator: "THEKIE",
                    lastModifiedBy: "THEKIE",
                    columnWidths,
                    cellTypes,
                });

                console.log("Excel 다운로드 완료:", fileName);
            } catch (error) {
                console.error("엑셀 다운로드 중 오류 발생:", error);
            }
        },
        async exportComparisonToExcel() {
            try {
                // 헤더 정의
                const headers = [
                    "날짜",
                    ...this.comparisonDynamicColumns.map((col) => `${col.key} (${this.findUnit(col.unit)})`),
                ];

                // 데이터 변환
                const excelData = this.comparisonTableDatas.map((row) => {
                    const rowData = {
                        날짜: row.regDt,
                    };

                    this.comparisonDynamicColumns.forEach((col) => {
                        const headerKey = `${col.key} (${this.findUnit(col.unit)})`;
                        rowData[headerKey] = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : "-";
                    });

                    return rowData;
                });

                // 컬럼 너비 설정
                const columnWidths = {
                    날짜: 20,
                };
                this.comparisonDynamicColumns.forEach((col) => {
                    const headerKey = `${col.key} (${this.findUnit(col.unit)})`;
                    columnWidths[headerKey] = 15;
                });

                // 셀 타입 설정
                const cellTypes = {
                    날짜: "string",
                };
                this.comparisonDynamicColumns.forEach((col) => {
                    const headerKey = `${col.key} (${this.findUnit(col.unit)})`;
                    cellTypes[headerKey] = "number";
                });

                const fileName = `${this.detailInfo.modelName}_comparison.xlsx`;
                const sheetName = `비교기간_${this.compareTitle}`;

                // excelHelper를 사용하여 워크북 생성 및 다운로드
                await excelHelper.createAndDownloadWorkbook(excelData, headers, fileName, {
                    sheetName,
                    creator: "THEKIE",
                    lastModifiedBy: "THEKIE",
                    columnWidths,
                    cellTypes,
                });

                console.log("Excel 다운로드 완료:", fileName);
            } catch (error) {
                console.error("엑셀 다운로드 중 오류 발생:", error);
            }
        },
    },
};
</script>

<style scoped>
.analysis-data-container {
    display: flex;
}
.data-table-container {
    width: 100%;
    font-size: 0.9rem;
    margin-bottom: 10px;
    box-shadow: rgba(17, 17, 26, 0.1) 0px 1px 0px;
}
.table-title {
    font-weight: 600;
    font-size: 14px;
}
.splitDiv {
    width: 20px;
}
</style>
